import { Component, forwardRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { moveItemInArray, CdkDropList, CdkDrag, CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import {
  ItemPrepGroupDialogComponent,
  ItemPrepGroupDialogData,
  ItemPrepGroupDialogResult,
} from '@components/item-prep-group-dialog/1.0';
import { MatDialog } from '@angular/material/dialog';
import { ConfigurationOrderItem } from '@generated/tcea/models/configuration-order-item';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TxpIntegersOnlyDirective } from '../../../directives/integers-only/1.0/txp-integers-only.directive';
import { NgIf, NgFor } from '@angular/common';
import { ButtonComponent } from '../../button/1.0/button.component';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';

@Component({
  selector: 'txp-dynamic-order-intervals-template-form',
  templateUrl: './dynamic-order-intervals-template-form.html',
  styleUrls: ['./dynamic-order-intervals-template-form.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicOrderIntervalsTemplateFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DynamicOrderIntervalsTemplateFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggle,
    MatFormField,
    MatLabel,
    MatInput,
    TxpIntegersOnlyDirective,
    NgIf,
    MatError,
    ButtonComponent,
    MatSelect,
    NgFor,
    MatOption,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    TranslateModule,
  ],
})
export class DynamicOrderIntervalsTemplateFormComponent
  implements ControlValueAccessor, OnInit, Validator
{
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize;
  exemptTypes = ['ANY', 'ONLY'];
  form: UntypedFormGroup;
  onTouched: any;
  disabled: boolean;
  onChangeFn: any;

  private unsubscribe = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {
    this.form = this.formBuilder.group({
      intervalsEnabled: new UntypedFormControl(false, Validators.required),
      orderThresholdAdditionalTime: new UntypedFormControl({ value: '', disabled: true }),
      orderThresholdInterval: new UntypedFormControl({ value: '', disabled: true }),
      orderThresholdMaxOrders: new UntypedFormControl({ value: '', disabled: true }),
      orderThresholdExemptItems: new UntypedFormControl([]),
      orderThresholdExemptionRule: new UntypedFormControl(''),
    });
  }

  ngOnInit() {
    this.form
      .get('intervalsEnabled')
      .valueChanges.subscribe((value) => this.handleIntervalFields(value));

    this.form.get('orderThresholdExemptItems').valueChanges.subscribe((items) => {
      if (this.form.disabled) {
        return;
      }

      const exemptionRuleControl = this.form.get('orderThresholdExemptionRule');

      if (items?.length > 0) {
        exemptionRuleControl.setValidators([Validators.required]);
      } else {
        exemptionRuleControl.clearValidators();
      }

      exemptionRuleControl.updateValueAndValidity();
    });
  }

  handleIntervalFields(value) {
    if (this.form.disabled) {
      return;
    }

    const controlKeys = [
      'orderThresholdAdditionalTime',
      'orderThresholdInterval',
      'orderThresholdMaxOrders',
    ];

    if (value) {
      controlKeys.forEach((key) => {
        const control = this.form.get(key);

        control.enable();
        control.setValidators([Validators.required, Validators.min(1)]);
        control.updateValueAndValidity();
      });
    } else {
      controlKeys.forEach((key) => {
        const control = this.form.get(key);

        control.disable();
        control.clearValidators();
        control.setValue('');
        control.updateValueAndValidity();
      });
    }
  }

  writeValue(obj): void {
    const newObj = DeepCloneService.clone({
      orderThresholdAdditionalTime: Number(obj.orderThresholdAdditionalTime) || null,
      orderThresholdInterval: Number(obj.orderThresholdInterval) || null,
      orderThresholdMaxOrders: Number(obj.orderThresholdMaxOrders) || null,
      orderThresholdExemptItems: obj.orderThresholdExemptItems || [],
      orderThresholdExemptionRule: obj.orderThresholdExemptionRule || null,
    });

    if (newObj && !newObj.orderThresholdExemptionRule) {
      newObj.orderThresholdExemptionRule = this.exemptTypes[0];

      if (newObj.orderThresholdExemptItems?.length > 0) {
        // if rule was changed and template has items, it means we need to make form dirty
        setTimeout(() => this.handleChange(newObj), 500);
      }
    }

    newObj.intervalsEnabled = !!(
      newObj.orderThresholdAdditionalTime ||
      newObj.orderThresholdInterval ||
      newObj.orderThresholdMaxOrders
    );

    this.handleIntervalFields(newObj.intervalsEnabled);

    this.form.reset(newObj, { emitEvent: false });
    this.handleChange(newObj);
  }

  handleChange(data) {
    if (this.onChangeFn) {
      const isEnabled = this.form.get('intervalsEnabled')?.value;
      this.onChangeFn({
        orderThresholdAdditionalTime: isEnabled
          ? Number(data.orderThresholdAdditionalTime) || null
          : null,
        orderThresholdInterval: isEnabled ? Number(data.orderThresholdInterval) || null : null,
        orderThresholdMaxOrders: isEnabled ? Number(data.orderThresholdMaxOrders) || null : null,
        orderThresholdExemptItems: isEnabled ? data.orderThresholdExemptItems || null : null,
        orderThresholdExemptionRule: isEnabled ? data.orderThresholdExemptionRule || null : null,
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.handleChange(this.form.getRawValue()));
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  onItemDrop(event) {
    const orderThresholdExemptItems = this.form.get('orderThresholdExemptItems').value;
    const previousIndex = orderThresholdExemptItems.findIndex(
      (entity) => entity.sku === event.item.data,
    );

    moveItemInArray(orderThresholdExemptItems, previousIndex, event.currentIndex);

    this.form.get('orderThresholdExemptItems').setValue(orderThresholdExemptItems);
  }

  handleDelete(entity: ConfigurationOrderItem) {
    const orderThresholdExemptItems = this.form.get('orderThresholdExemptItems').value;

    orderThresholdExemptItems.splice(orderThresholdExemptItems.indexOf(entity), 1);

    this.form.get('orderThresholdExemptItems').setValue(orderThresholdExemptItems);
  }

  handleAddEntity() {
    const dialogData: ItemPrepGroupDialogData = {
      name: '',
      items: this.form.get('orderThresholdExemptItems').value,
      nameNotRequired: true,
      translations: {
        title: `${this.translateService.instant(
          'store.dynamicPrepTime.prepGroupModal.manageExemptProducts',
        )}`,
        type: this.translateService.instant('store.dynamicPrepTime.prepGroupModal.products'),
        group: '',
        listTitle: this.translateService.instant('store.dynamicPrepTime.prepGroupModal.product'),
      },
    };

    const dialogRef = this.dialog.open(ItemPrepGroupDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: ItemPrepGroupDialogResult) => {
      // result undefined means it was cancelled
      if (result) {
        const newSelectedItems = result.items.map((item) => ({
          name: item.name,
          sku: item.catalogEntryId,
        }));

        this.form.get('orderThresholdExemptItems').setValue(newSelectedItems);
      }
    });
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid
      ? undefined
      : {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.fieldsAreInvalid'),
          },
        };
  }
}
